import React, { useState, useCallback, useEffect, useRef } from "react";
import { useMediaQuery } from "react-responsive";
import ContainerCard from "./containerCard";
import { SimpleTabs } from "./tabs";
import SectionHeading from "./sectionHeading";
import { Button, ArrowLeft, Circle } from "@speedoc/design-assets";
import { Link } from "react-router-dom";

const DesktopTable = ({ content }) => {
  const [activeIndex, setActiveIndex] = useState(0);

  const toggleTab = index => {
    setActiveIndex(index);
  };

  const buttonDetails = content.tabs.map(({ title }, index) => {
    return {
      text: title,
      classes: `secondary sm ${activeIndex === index ? "active" : ""}`
    };
  });

  return (
    <div id="panelsTable">
      {content.title && <SectionHeading title={content.title} subtitle={content.subtitle} />}
      <ContainerCard>
        <SimpleTabs flexDirection="row" buttonDetails={buttonDetails} onClick={toggleTab} className="tabs" />
        <div className="main">
          {content.panels.map(
            (panel, index) =>
              activeIndex === index && (
                <div key={index} className="panel">
                  {panel.table.rows.map((row, j) => (
                    <div key={j} className="row">
                      {row.cells.map((cell, k) => (
                        <div key={k} className="cell">
                          {cell.content}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )
          )}
        </div>
      </ContainerCard>
      <div className="footerWrap">
        {content.footnote && <div className="footnote" dangerouslySetInnerHTML={{ __html: content.footnote }} />}
        <a className="downloadApp" href="download">
          Get the App
        </a>
      </div>
    </div>
  );
};

const ExpandedCardButtons = ({ content, onClick, mobile }) => {
  const scrollToForm = () => {
    let form;
    try {
      form = document.querySelector("#contactFormAnchor");
      const topPoint = form.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({ behavior: "smooth", left: 0, top: topPoint });
    } catch {
      return;
    }
  };

  return (
    <div className="cardExpandedBottomButtons">
      <div className="buttonsContainer">
        <div className="buttons">
          <div className={`linkPrimary ${mobile ? "" : "sm"}`} arrow="right" onClick={scrollToForm}>
            <span>{mobile ? content?.expandedButtonDetailsTitleMobile : content?.expandedButtonDetailsTitle}</span>
          </div>
          {content?.linkToExploreTitle && (
            <Link to={content.linkToExploreUrl}>
              <Button className="secondary sm">
                <span>{content.linkToExploreTitle}</span>
              </Button>
            </Link>
          )}
        </div>
      </div>
      {!mobile && (
        <div className="collapseContainer" onClick={onClick}>
          <ArrowLeft className="arrow left" />
          <span>{content?.expandedCollapse}</span>
        </div>
      )}
    </div>
  );
};

const ExpandedCardBottomInfo = ({ content, services }) => {
  return (
    <div className="expandedCardBottomInfo">
      <div className="priceInfo">
        {(content?.priceBox || []).map((box, i) => (
          <div key={i} className="box">
            <span>{box.currency}</span>
            <span className="amount"> {box.price}</span>
            <span className="info"> {box.info}</span>
            {i < content.priceBox.length - 1 && <span> /&nbsp;</span>}
          </div>
        ))}
      </div>
      <div className="packageDetailsList">
        <div dangerouslySetInnerHTML={{ __html: (services && services[0].text) || "" }} />
        <div className="horizontalList">
          {services &&
            services.slice(1, -1).map(item => {
              return <div key={item.text} dangerouslySetInnerHTML={{ __html: item.text }} />;
            })}
        </div>
        <div
          dangerouslySetInnerHTML={{
            __html: (services && services.length > 1 && services[services.length - 1].text) || ""
          }}
        />
      </div>
    </div>
  );
};

const checkServiceSize = content => {
  return content.filter(service => service.title.length > 80).length > 0;
};

const PackageDetails = ({ content, fade }) => {
  return (
    <div className={`packageDetails ${fade ? "fadeIn" : ""} ${checkServiceSize(content) ? "longString" : ""}`}>
      {content.map(service => {
        return (
          <div key={service.title} className="serviceListItem">
            <div className="serviceName">
              <Circle /> <p>{service.title}</p>
            </div>
            {service.entries ? (
              <div className="serviceDetails">
                {service.entries.map(entry => {
                  return <p key={entry.text}>{entry.text}</p>;
                })}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
};

const MobileCard = ({ content, className, handleClick, index, open }) => {
  const [displayBody, setDisplayBody] = useState(false);
  const [hidePreview, setHidePreview] = useState(false);
  const [fadeBody, setFadeBody] = useState(false);
  const [activeTab] = useState(0);
  const [fade] = useState(true);

  let dataAttrs = {
    "data-card": `card-${index}`
  };

  const closeCard = e => {
    setFadeBody(prevState => !prevState);
    setTimeout(() => {
      showBody(350).then(() => {
        handleClick(e, index);
      });
    }, 200);
  };

  const showBody = useCallback((delay = 900) => {
    setHidePreview(prevState => !prevState);
    return new Promise(resolve => {
      setTimeout(() => {
        setDisplayBody(prevState => !prevState);
        resolve();
      }, delay);
    });
  }, []);

  useEffect(() => {
    if (open) {
      showBody().then(() =>
        setTimeout(() => {
          setFadeBody(prevState => !prevState);
        }, 200)
      );
    }
  }, [open, showBody]);

  return (
    <ContainerCard dataAttrs={dataAttrs} className={`mobileCard ${className}  ${open ? "expand" : ""}`}>
      <h2 className="title">{content.previewTitle}</h2>
      {displayBody ? (
        <div className={`cardBody ${fadeBody ? "display" : ""}`}>
          <PackageDetails content={content.tabPackageCards[activeTab]?.expandedList || []} fade={fade} />
          <ExpandedCardBottomInfo
            content={content.tabPackageCards[activeTab]}
            services={content.tabPackageCards[activeTab]?.expandedIncludesList}
          />
          <ExpandedCardButtons content={content.tabPackageCards[activeTab]} onClick={closeCard} mobile={true} />
        </div>
      ) : (
        <div className={`cardPreview ${hidePreview ? "hide" : ""}`}>
          <div className="cardDetails">
            <p className="priceInfo">
              <span className="amount"> {content.previewPrice}</span>
            </p>
          </div>
          <div className="servicesListSmall">
            {content.tabPackageCards[activeTab]?.expandedIncludesList?.map(item => (
              <div key={item.text} dangerouslySetInnerHTML={{ __html: item.text }} />
            ))}
          </div>
          <div className="btn-bottom">
            <Button onClick={handleClick} type="button" className="secondary sm">
              <span>{content.previewButtonTitle}</span>
            </Button>
          </div>
        </div>
      )}
      {content.expandedImage?.url && (
        <img className="cardImage" src={content.expandedImage?.url} alt={content.expandedTitle} />
      )}
    </ContainerCard>
  );
};

const MobileTable = ({ content }) => {
  const [openCardIndex, setOpenCardIndex] = useState(undefined);
  const container = useRef();

  const isSafari = () => {
    return (
      navigator.vendor &&
      navigator.vendor.indexOf("Apple") > -1 &&
      navigator.userAgent &&
      navigator.userAgent.indexOf("CriOS") === -1 &&
      navigator.userAgent.indexOf("FxiOS") === -1
    );
  };

  function scrollIosScreen(position, time = 250) {
    return new Promise(resolve => {
      var scrollY = position / 50;
      var curTime = 0;
      while (curTime <= time) {
        window.setTimeout(() => {
          window.scrollBy({ left: 0, top: scrollY });
        }, curTime);
        curTime += time / 50;
      }
      resolve();
    });
  }

  const scrollScreen = position => {
    window.scrollBy(0, position);
    return new Promise(resolve => {
      setTimeout(resolve, 300);
    });
  };

  const updateIndexes = index => {
    setOpenCardIndex(index);
    return new Promise(resolve => {
      setTimeout(resolve, 300);
    });
  };

  const safariScrollPosition = position => {
    const navBarHeight = document.querySelector("#navigationMobile").offsetHeight;
    if (position < 1) {
      return -(-position + navBarHeight);
    } else if (position <= navBarHeight) {
      return -(position + navBarHeight);
    } else {
      return position - navBarHeight;
    }
  };

  const openCard = async (position, index) => {
    if (isSafari()) {
      scrollIosScreen(safariScrollPosition(position), 450, "top").then(() =>
        setTimeout(() => {
          updateIndexes(index);
        }, 400)
      );
    } else {
      scrollScreen(position - 99).then(() => updateIndexes(index));
    }
  };

  const handleClick = async (e, index) => {
    const card = container.current.querySelector(`[data-card='card-${index}']`);
    const cardTop = card.getBoundingClientRect().top;
    if (openCardIndex !== index) {
      setOpenCardIndex(index);
      if (isSafari()) {
        scrollIosScreen(safariScrollPosition(cardTop), 450, "top");
      } else {
        scrollScreen(cardTop - 99);
      }
      return;
    }
    openCard(cardTop, index);
  };

  return (
    <div id="panelsTable">
      {content.titleMobile && (
        <div className="timeDescription">
          <SectionHeading title={content.titleMobile} />
          {content.timeDescriptionMobile && <span>{content.timeDescriptionMobile} </span>}
        </div>
      )}
      {content.title && <SectionHeading title={content.title} subtitle={content.subtitle} />}
      <div>
        <div ref={container} className="cardsContainer">
          {(content.mobileCards || []).map((cardContent, index) => (
            <MobileCard
              key={index}
              index={index}
              handleClick={e => handleClick(e, index)}
              open={openCardIndex === index}
              className="card"
              content={cardContent}
            />
          ))}
        </div>
      </div>
      {content.priceDescriptionMobile && <div dangerouslySetInnerHTML={{ __html: content.priceDescriptionMobile }} />}
    </div>
  );
};

const Table = ({ content }) => {
  const isMobile = useMediaQuery({ query: "(max-width: 576px)" });

  if (isMobile) {
    return <MobileTable content={content} />;
  }

  return <DesktopTable content={content} />;
};

export default Table;
